/*
 * chatSwipeFlow — the scripted step engine behind the in-chat advisor-matching
 * experience. Pure, no React: ChatScreen.js drives it and renders whatever card
 * each step returns via components/SwipeCards.js. Deterministic on purpose (per
 * the approved plan) — this is a client-side script producing real chat bubbles,
 * not an LLM-authored conversation, so the flow is reliable and demoable.
 *
 * Reuses the same advisor data/scoring as the standalone Swipe screen
 * (constants/advisors.js) and the same appointment-slot logic as
 * BookAppointmentScreen.js (constants/appointmentSlots.js) so there's one
 * implementation of each, not two.
 */
import { ADVISORS, classifyAdvisorType, matchAdvisors, STYLE_OPTIONS } from '../../constants/advisors';
import { CHANNELS, nextSlots } from '../../constants/appointmentSlots';

export const PERSONALITY_KEY = 'swipe_advisor_style';

// Steps where the real chat text input (not a card button) is a valid answer.
export function stepAcceptsFreeText(step) {
  return step === 'what-brings-you';
}

// ─── internal helpers ─────────────────────────────────────────────────────

function isRecent(isoStr, days) {
  if (!isoStr) return false;
  return (Date.now() - new Date(isoStr).getTime()) < days * 24 * 60 * 60 * 1000;
}

function styleOptionFor(label) {
  return STYLE_OPTIONS.find((s) => s.label === label) || null;
}

function stylePickerResult(answers) {
  return {
    nextState: { step: 'style-picker', answers },
    botMessage: "One more thing — how would you like your advisor to approach conversations?",
    card: { type: 'choice', options: STYLE_OPTIONS, skippable: true },
  };
}

function advisorCarouselResult(answers) {
  const style = styleOptionFor(answers.style);
  const advisorType = classifyAdvisorType(answers.intent || '');
  const pool = answers.advisorTypeFilter
    ? ADVISORS.filter(a => (a.specialties || []).includes(answers.advisorTypeFilter) || (a.specialties || []).includes('general'))
    : ADVISORS;
  const ranked = matchAdvisors(pool, { category: answers.category, style, snapshotBracket: answers.snapshotBracket, advisorType });
  const top3 = ranked.slice(0, 3);
  return {
    nextState: { step: 'advisor-carousel', answers },
    botMessage: `Here are your top ${top3.length} matches — swipe through and pick the one that feels right.`,
    card: { type: 'advisor-carousel', advisors: top3 },
  };
}

function advisorOfferResult(answers) {
  return {
    nextState: { step: 'advisor-offer', answers },
    botMessage: "Was that helpful? I can also connect you with a TD advisor if you'd like a human touch.",
    card: {
      type: 'choice',
      options: [
        { id: 'no', label: "I'm good, thanks!" },
        { id: 'yes', label: 'Connect me with an advisor' },
      ],
    },
  };
}

// ─── entry points ──────────────────────────────────────────────────────────

// seedIntent: when kicked off from an AdvisorCtaCard ("Connect" button), the
// user has already signalled they want a human — skip IVA self-service entirely
// and go straight to the advisor carousel.
//
// No seedIntent (SWIPE_SEED path): IVA asks what the user needs in plain text,
// handles the question itself via LLM, then offers to connect to an advisor.
//
// swipePrefs: { incomeLastRefreshed, snapshotBracket, ... } from GET /api/swipe/preferences.
export function startSwipeFlow(seedIntent, swipePrefs, { advisorTypeFilter } = {}) {
  const incomeSkip = isRecent(swipePrefs?.incomeLastRefreshed, 30);
  const stored = typeof window !== 'undefined' ? window.localStorage.getItem(PERSONALITY_KEY) : null;
  const baseAnswers = {
    incomeSkip,
    snapshotBracket: incomeSkip ? (swipePrefs?.snapshotBracket || null) : undefined,
    advisorTypeFilter: advisorTypeFilter || null,
    style: stored || null,
  };

  if (seedIntent) {
    // ADVISOR_CTA path: user already asked for a human. If no cached style preference,
    // collect it before the carousel — otherwise go straight.
    const answersWithIntent = { ...baseAnswers, intent: seedIntent };
    if (!baseAnswers.style) return stylePickerResult(answersWithIntent);
    return advisorCarouselResult(answersWithIntent);
  }

  // SWIPE_SEED path: IVA tries to help first; advisor offer comes after LLM responds.
  return {
    nextState: { step: 'what-brings-you', answers: baseAnswers },
    botMessage: "What brings you in today? I'll do my best to help — and if it makes sense, I can connect you with a TD advisor.",
    card: null,
  };
}

// state: { step, answers }. answer: whatever the active card/free-text produced.
export function advanceSwipeFlow(state, answer) {
  const { step, answers } = state;

  switch (step) {
    case 'what-brings-you':
      // User typed their need — LLM will handle the response; we move to
      // llm-pending so ChatScreen knows to show the advisor-offer card once
      // the LLM finishes. skipBotMessage tells advanceSwipe not to add a
      // synthetic bot bubble (the real LLM response IS the bot message).
      return {
        nextState: { step: 'llm-pending', answers: { ...answers, intent: answer } },
        botMessage: '',
        card: null,
        skipBotMessage: true,
      };

    case 'llm-pending':
      return advisorOfferResult(answers);

    case 'style-picker': {
      // answer is opt.id (e.g. 'direct'); look up label to match SwipeScreen.js localStorage format.
      const styleEntry = STYLE_OPTIONS.find(s => s.id === answer);
      const styleLabel = styleEntry?.label || null;
      if (styleLabel && typeof window !== 'undefined') {
        window.localStorage.setItem(PERSONALITY_KEY, styleLabel);
      }
      return advisorCarouselResult({ ...answers, style: styleLabel });
    }

    case 'advisor-offer':
      if (answer === 'no') {
        return { nextState: state, botMessage: "Great — I'm here whenever you need anything else.", card: null, done: true };
      }
      // Ask about communication style before showing carousel if not already known.
      if (!answers.style) return stylePickerResult(answers);
      return advisorCarouselResult(answers);

    case 'advisor-carousel':
      return {
        nextState: { step: 'scheduler', answers: { ...answers, advisor: answer } },
        botMessage: `Great choice! When would you like to meet with ${answer.name.split(' ')[0]}?`,
        card: { type: 'scheduler', advisor: answer, slots: nextSlots(4), channels: CHANNELS },
      };

    case 'scheduler': {
      const nextAnswers = { ...answers, slot: answer.slot, channel: answer.channel };
      return {
        nextState: { step: 'confirmation', answers: nextAnswers },
        botMessage: "You're booked! Here's your confirmation.",
        card: {
          type: 'confirmation',
          advisor: nextAnswers.advisor,
          slot: nextAnswers.slot,
          channel: nextAnswers.channel,
        },
        done: true,
      };
    }

    default:
      return { nextState: state, botMessage: '', card: null, done: true };
  }
}
